import * as R from 'ramda';
import * as RA from 'ramda-adjunct';

import { WhitelabelConfigs } from 'reactApp/modules/WhiteLabelModule/WhiteLabelConfigs';

const getFormErrors = (errorResponseBody, errorTexts) => {
    const shouldCheckFieldTexts = Boolean(errorTexts);

    const errors = [].concat(errorResponseBody).filter(R.identity);

    const fieldErrors = {};
    const commonErrors = [];

    errors.forEach((errorObj) => {
        const error = _parseError(errorObj.error);
        const key = errorObj.key;
        const hasKey = key != null;
        let goesToFieldTexts = hasKey;
        if (hasKey && shouldCheckFieldTexts) {
            const hasSuchFieldInTexts = errorTexts.fieldTexts[key] != null;
            const hasSuchErrorInCommonTexts =
                errorTexts.commonTexts[error] != null;
            if (!hasSuchFieldInTexts && hasSuchErrorInCommonTexts) {
                goesToFieldTexts = false;
            }
        }

        if (hasKey && goesToFieldTexts) {
            fieldErrors[key] = error;
        } else if (error) {
            commonErrors.push(error);
        }
    });

    if (!commonErrors.length && !Object.keys(fieldErrors).length) {
        commonErrors.push('default');
    }

    return {
        fieldErrors,
        commonErrors,
    };
};

const _parseError = (error) => {
    return (error || '').toLowerCase();
};

const getCommonErrors = (errorResponseBody) => {
    const { commonErrors } = getFormErrors(errorResponseBody);
    if (!commonErrors.length) {
        return ['default'];
    }
    return commonErrors;
};

const getCommonErrorMessages = ({ errors, texts }) => {
    return getErrorMessages({ errors, texts });
};

const getFieldErrorMessages = ({ errors, texts }) => {
    return R.mapObjIndexed((error, fieldKey) => {
        const fieldTexts = texts[fieldKey] || {};
        if (fieldTexts.default == null && texts.default != null) {
            fieldTexts.default = texts.default;
        }

        return getErrorMessages({ errors: [error], texts: fieldTexts })[0];
    })(errors);
};

const getErrorMessages = ({ errors, texts }) => {
    return errors.map((error) => {
        return texts[error] || texts.default;
    });
};

const getFormErrorMessages = ({
    fieldErrors,
    commonErrors,
    fieldTexts,
    commonTexts,
}) => {
    const { fieldErrorsFiltered, excludingKeys } = handleUnknownFieldMessages({
        fieldErrors,
        fieldTexts,
    });

    const commonMessages = getCommonErrorMessages({
        errors: commonErrors,
        texts: commonTexts,
    });

    const fieldMessages = getFieldErrorMessages({
        errors: fieldErrorsFiltered,
        texts: fieldTexts,
    });

    if (excludingKeys.length && !commonMessages.length) {
        commonMessages.push(commonTexts.default);
    }

    return { commonMessages, fieldMessages };
};

const handleUnknownFieldMessages = ({ fieldErrors, fieldTexts }) => {
    const excludingKeys = R.keys(fieldErrors).filter(
        (field) => !fieldTexts[field],
    );
    const fieldErrorsFiltered = RA.omitBy(
        (val, key) => excludingKeys.includes(key),
        fieldErrors,
    );
    return {
        excludingKeys,
        fieldErrorsFiltered,
    };
};

const getFormMessagesFromResponse = (
    response,
    { fieldTexts, commonTexts },
    { captchaFieldKey = 'captcha', noCaptchaUrlHandling = false } = {},
) => {
    const { fieldErrors, commonErrors } = McsErrors.getFormErrors(
        response.body,
        { fieldTexts, commonTexts },
    );
    const errorMessages = getFormErrorMessages({
        fieldErrors,
        commonErrors,
        fieldTexts,
        commonTexts,
    });
    if (!noCaptchaUrlHandling) {
        const captchaMessage = errorMessages.fieldMessages[captchaFieldKey];
        const providedCaptchaMessages = R.values(fieldTexts[captchaFieldKey]);

        if (
            response.body.value &&
            captchaMessage &&
            providedCaptchaMessages.includes(captchaMessage)
        ) {
            errorMessages.fieldMessages[
                captchaFieldKey
            ] += ` | ${response.body.value}`;
        }
    }
    return errorMessages;
};

const getCommonMessagesFromResponse = (response, texts) => {
    const errors = McsErrors.getCommonErrors(response.body);
    return getCommonErrorMessages({
        errors,
        texts,
    });
};

// its LinkCatalog.getHelpLink copy-paste, because circular dependency
function getHelpLink() {
    const whitelabelConfig = WhitelabelConfigs[process.env.WHITELABEL_NAME];
    return (
        whitelabelConfig?.support_contacts?.help_link ||
        'https://mcs.mail.ru/help/infra'
    );
}

const TRY_LATER_HTML = `Попробуйте позже или обратитесь в <a href=${getHelpLink()} rel="noopener" target="_blank">службу поддержки</a>.`;

const CALL_SUPPORT_HTML = `Пожалуйста, обратитесь в <a href=${getHelpLink()} rel="noopener" target="_blank">службу поддержки</a>.`;

const handleErrorMessagesInForm = ({
    errorMessages,
    formikParams: { errors, setErrors },
    setCaptchaUrlToState = undefined,
    captchaFieldKey = 'captcha',
}) => {
    const gotInvalidErrorMessages =
        !errorMessages.fieldMessages || !errorMessages.commonMessages;
    if (gotInvalidErrorMessages) {
        errorMessages = {
            commonMessages: [
                `Произошла непредвиденная ошибка. ${TRY_LATER_HTML}`,
            ],
            fieldMessages: {},
        };
    }

    if (setCaptchaUrlToState) {
        const {
            errorMessages: handledErrorMessages,
            captchaUrl,
        } = McsErrors.handleErrorMessagesWithCaptchaUrl(errorMessages, {
            captchaFieldKey,
        });

        if (captchaUrl) {
            setCaptchaUrlToState(`${captchaUrl}?${Date.now()}`);
        } else {
            setCaptchaUrlToState();
        }

        errorMessages = handledErrorMessages;
    }

    const { fieldMessages, commonMessages } = errorMessages;

    setErrors({
        ...errors,
        ...fieldMessages,
        commonErrorMessages: commonMessages.length ? commonMessages : undefined,
    });
};

const handleErrorMessagesWithCaptchaUrl = (
    errorMessages,
    { captchaFieldKey = 'captcha' } = {},
) => {
    const captchaMessage = errorMessages.fieldMessages[captchaFieldKey];
    let captchaUrl = '';
    if (captchaMessage) {
        const [message, url] = captchaMessage.split(' | ');
        captchaUrl = url;
        errorMessages.fieldMessages[captchaFieldKey] = message;
    }

    return { errorMessages, captchaUrl };
};

export const transformErrorResponseKey = (error, transformations) => {
    const keys = Object.keys(transformations);

    if (error.body && keys.includes(error.body.key)) {
        return {
            ...error,
            body: {
                ...error.body,
                key: transformations[error.body.key],
            },
        };
    }
    return error;
};

const CAPTCHA_ERROR_MESSAGES = {
    argument_required: 'Введите код с картинки',
    argument_invalid: 'Неверный код с картинки',
    too_many_requests: 'Введите код с картинки',
};

export const McsErrors = {
    getFormErrors,
    getCommonErrors,
    getCommonErrorMessages,
    getFieldErrorMessages,
    getFormErrorMessages,
    getFormMessagesFromResponse,
    getCommonMessagesFromResponse,
    TRY_LATER_HTML,
    CALL_SUPPORT_HTML,
    handleErrorMessagesInForm,
    handleErrorMessagesWithCaptchaUrl,
    CAPTCHA_ERROR_MESSAGES,
    transformErrorResponseKey,
};
